<script setup lang="ts">
import {type QuestionNumeric, QuestionnaireType as QuestionnaireDisplayType} from '@/ts/types/dto/interventions-dto';
import {
  computed,
  onBeforeMount,
  ref,
  watch,
} from 'vue';
import {
  type QuestionAnswerNumericBody,
  QuestionnaireType,
} from '@/ts/types/dto/session-request-dto';
import BadgePill from '@/vue/atoms/badge-pill.vue';
import {useUserStore} from '@/ts/store/user-store';
import FormRequired from '@/vue/atoms/form-required.vue';
import QuestionWeightInput from './question-weight-input.vue';
import QuestionHeightInput from './question-height-input.vue';
import WeightLossCalculationDisplay from '@/vue/molecules/components/question/weight-loss-calculation-display.vue';
import {formatZipCode, getRandomId, roundTo} from '@/ts/utils/pure-functions';

const props = defineProps<{
  question: QuestionNumeric;
  questionnaireType?: string;
}>();

const model = defineModel<QuestionAnswerNumericBody|null>();

const {userData} = useUserStore();

const format = computed(() => props.question.numeric.format);

const isSpecialFormat = computed(() => {
  return format.value === 'weight' || format.value === 'height';
});

const min = computed(() => {
  return props.question.numeric.minValue ?? 0;
});

const max = computed(() => {
  return props.question.numeric.maxValue ?? 0;
});

const isIntegerLike = computed(() => format.value === 'integer' || format.value === 'zipcode');

const step = computed(() => (isIntegerLike.value ? 1 : 0.01));

const decimals = computed(() => (isIntegerLike.value ? 0 : 2));

const minDisplay = computed(() => {
  return format.value === 'zipcode'
    ? formatZipCode(min.value)
    : min.value.toFixed(decimals.value);
});

const maxDisplay = computed(() => {
  return format.value === 'zipcode'
    ? formatZipCode(max.value)
    : max.value.toFixed(decimals.value);
});

onBeforeMount(() => {
  // Force reactive trigger for iOS Safari when model already has data —
  // without this, the component can render as empty comment nodes when
  // remounted inside a dialog (e.g., navigating back to a previous question)
  if (model.value) {
    model.value = {...model.value};
    return;
  }
  // First, check if there's a previously answered value to restore
  if (props.question.numericSelected !== null && props.question.numericSelected !== void 0) {
    model.value = {
      numeric: props.question.numericSelected,
      type: QuestionnaireType.NUMERIC,
    };
  } else if (!model.value && !isSpecialFormat.value) {
    const birthYearPreset: number = userData?.birthYear ?? 1990;
    const isBirthYear = min.value >= 1900 && birthYearPreset && birthYearPreset >= min.value && birthYearPreset <= max.value;

    // special case: year of birth - use user's birth year as default
    if (isBirthYear) {
      model.value = {
        numeric: birthYearPreset,
        type: QuestionnaireType.NUMERIC,
      };
    }
    // For other numeric questions (like zip code), DO NOT auto-initialize
    // The min value is just a validation constraint, not a meaningful default
  }
});

// Seed display refs synchronously from the saved answer (model, or
// question.numericSelected when re-opening — promoted to model later in
// onBeforeMount). Required because q-input's mask="#####" doesn't re-apply
// when model-value flips post-mount, so a watcher-only sync paints empty on first visit.
const initialNumeric = model.value?.numeric ?? props.question.numericSelected ?? void 0;

const inputValue = ref<number | undefined>(initialNumeric ?? void 0);

// String-backed display for the zipcode branch — keeps leading zeros visible
// while typing and restores the zero-padded form on blur and on remount.
const zipDisplay = ref<string>(
  format.value === 'zipcode' && typeof initialNumeric === 'number'
    ? formatZipCode(initialNumeric)
    : '',
);

// Sync inputValue (or zipDisplay for zipcode) with the model on subsequent
// changes (programmatic prefill from onBeforeMount, parent updates, etc.).
// The initial value is seeded above; this watcher is not `immediate` because
// firing it at setup with the still-null model would clobber that seed back
// to '' before q-input's first render.
watch(() => model.value?.numeric, (newVal) => {
  if (format.value === 'zipcode') {
    if (newVal === null || newVal === void 0) {
      zipDisplay.value = '';
    } else if (parseInt(zipDisplay.value, 10) !== newVal) {
      zipDisplay.value = formatZipCode(newVal);
    }
    return;
  }
  if (inputValue.value !== newVal) {
    inputValue.value = newVal;
  }
});

// Watch inputValue and update model (without auto-correction).
// Skipped for zipcode — that branch updates the model via onZipInput directly.
watch(inputValue, (newVal) => {
  if (format.value === 'zipcode') {
    return;
  }
  if (typeof newVal === 'undefined' || newVal === null || Number.isNaN(newVal)) {
    model.value = null;
    return;
  }

  const valNum = isIntegerLike.value ? Math.round(newVal) : roundTo(newVal, decimals.value);

  // Update the input if it was rounded (but not clamped)
  if (isIntegerLike.value && valNum !== newVal) {
    inputValue.value = valNum;
  }

  model.value = {
    numeric: valNum,
    type: QuestionnaireType.NUMERIC,
  };
});

function onZipInput(val: string | number | null): void {
  const raw = val === null || val === void 0 ? '' : String(val);
  const digits = (raw?.replace(/\D/gu, '') ?? '').slice(0, 5);
  zipDisplay.value = digits;
  if (digits === '') {
    model.value = null;
    return;
  }
  model.value = {
    numeric: parseInt(digits, 10),
    type: QuestionnaireType.NUMERIC,
  };
}

function onZipBlur(): void {
  if (model.value?.numeric === null || model.value?.numeric === void 0) {
    return;
  }
  zipDisplay.value = formatZipCode(model.value.numeric);
}

const isValid = computed((): boolean => {
  return Boolean(model.value) &&
    model.value!.numeric >= min.value &&
    model.value!.numeric <= max.value;
});

const inputLabelledby = `question-${getRandomId()}`;

const isWeightGoalQuestionnaire = computed(() => {
  return props.questionnaireType === QuestionnaireDisplayType.WEIGHT_LOSS_PERCENT;
});

const selectedPercentage = computed(() => {
  return model.value?.numeric ?? null;
});

</script>
<template>
  <div>
    <question-weight-input
      v-if="format === 'weight'"
      v-model="model"
      :question="question"
    />
    <question-height-input
      v-else-if="format === 'height'"
      v-model="model"
      :question="question"
    />
    <div v-else class="question-numeric-component">
      <badge-pill theme="blue" :label="$t('question')"/>
      <div :id="inputLabelledby" v-safe-html="question.name" class="question text-h6"/>
      <q-input
        v-if="format === 'zipcode'"
        :model-value="zipDisplay"
        class="input"
        type="text"
        inputmode="numeric"
        mask="#####"
        :aria-labelledby="inputLabelledby"
        @update:model-value="onZipInput"
        @blur="onZipBlur"
      />
      <q-input
        v-else
        v-model.number="inputValue"
        class="input"
        type="number"
        :step="step"
        :aria-labelledby="inputLabelledby"
      />
      <form-required
        id="sdf"
        :valid="isValid"
        :message="$t('validation_number_error_msg', {
          min: minDisplay,
          max: maxDisplay,
        })"
      />
    </div>
    <weight-loss-calculation-display
      v-if="isWeightGoalQuestionnaire"
      :selected-percentage="selectedPercentage"
    />
  </div>
</template>
<style scoped lang="scss">
.question-numeric-component {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-spacing-4);
}

.question {
  margin: 0;
  text-align: center;
  max-width: 1000px;
  @include font-display-3;

  :deep(p) {
    margin: 0;
  }
}

.input {
  min-width: 100px;
}

.input :deep(input) {
  text-align: center;
}

</style>
